<template>
  <tr :class="rowClass">
    <template v-if="isEditing">
      <td
        v-if="isNew"
        v-t="'user.field.auto-generated'"
        class="cell-text is-placeholder"
      />
      <td v-else class="cell-text">{{ user.loginName }}</td>

      <td>
        <div class="cell-controls">
          <div class="control input--name">
            <input
              v-model="user.firstName"
              v-validate.immediate
              :disabled="isLoading"
              name="firstName"
              maxlength="50"
              required
              placeholder="Marius"
              class="input"
            />
          </div>
          <div class="control input--name">
            <input
              v-model="user.lastName"
              v-validate.immediate
              :disabled="isLoading"
              name="lastName"
              maxlength="50"
              required
              placeholder="Musterperson"
              class="input"
            />
          </div>
        </div>
      </td>

      <td>
        <div class="control">
          <input
            v-model="user.email"
            v-validate.immediate
            :disabled="isLoading"
            name="email"
            type="email"
            maxlength="255"
            required
            placeholder="marius.musterperson@provider.internet"
            class="input"
          />
        </div>
      </td>

      <td
        v-if="isNew"
        v-t="'user.field.auto-generated'"
        class="cell-text is-placeholder"
      />
      <td
        v-else
        :class="{ 'is-placeholder': !user.password }"
        class="cell-text"
      >
        {{ user.password || $t("user.field.hidden") }}
      </td>

      <td class="cell-text">
        <div class="buttons">
          <!-- Workaround for bulma bug #1150: .has-text-white -->
          <r-button
            :disabled="!isFormValid || isLoading"
            :loading="isSaving"
            size="small"
            prominent
            class="has-text-white"
            @click="saveUser"
          >
            {{ isNew ? $t("user.button.create") : $t("user.button.save") }}
          </r-button>
          <r-button
            v-t="'user.button.abort'"
            :disabled="isLoading"
            size="small"
            class="is-danger has-text-white"
            @click="cancelEdit()"
          />
        </div>
      </td>
    </template>
    <template v-else>
      <td :class="{ 'is-placeholder': !user.active }">
        {{ user.loginName }}
        <span v-if="!user.active"> ({{ $t("user.disabled") }})</span>
      </td>
      <td :class="{ 'is-placeholder': !user.active }">
        {{ user.firstName }} {{ user.lastName }}
      </td>
      <td :class="{ 'is-placeholder': !user.active }">{{ user.email }}</td>
      <td :class="{ 'is-placeholder': !user.password }">
        {{ user.password || $t("user.field.hidden") }}
      </td>
      <td>
        <div class="buttons">
          <r-button
            v-t="'user.button.edit'"
            :disabled="isLoading"
            size="small"
            @click="editUser"
          />
          <r-button
            v-t="'user.button.new-password'"
            :loading="isRequestingPassword"
            :disabled="isLoading"
            size="small"
            @click="requestPassword"
          />

          <r-button
            v-if="user.active"
            :disabled="isLoading"
            :loading="isDeactivating"
            size="small"
            @click="disableUser"
          >
            {{ $t("user.button.disable") }}
          </r-button>
          <r-button
            v-else
            :disabled="isLoading"
            :loading="isActivating"
            size="small"
            @click="enableUser"
          >
            {{ $t("user.button.enable") }}
          </r-button>
        </div>
      </td>
    </template>
  </tr>
</template>

<script>
export default {
  props: {
    value: {
      type: Object,
      required: true
    },
    new: {
      type: Boolean,
      default: false
    },
    branch: {
      type: Object,
      required: true
    }
  },
  data() {
    if (this.new) {
      this.$emit("locked");
    }
    return {
      user: { ...this.value },
      isNew: this.new,
      isEditing: this.new,
      isSaving: false,
      isRequestingPassword: false,
      isActivating: false,
      isDeactivating: false,
      error: null
    };
  },
  computed: {
    rowClass() {
      return {
        "is-danger": this.error,
        "is-selected": this.isEditing
      };
    },
    isLoading() {
      return this.isSaving || this.isRequestingPassword;
    },
    isFormValid() {
      return !this.errors.any();
    }
  },
  methods: {
    editUser() {
      this.isEditing = true;
      this.$emit("locked");
    },
    async saveUser() {
      if (this.isSaving) {
        return;
      }

      this.isSaving = true;
      this.error = null;

      try {
        if (this.isNew) {
          await this.$store.dispatch("createNewUser", {
            branch: this.branch,
            user: {
              firstName: this.user.firstName,
              lastName: this.user.lastName,
              email: this.user.email
            }
          });
        } else {
          await this.$store.dispatch("saveUser", {
            branch: this.branch,
            user: {
              loginName: this.user.loginName,
              firstName: this.user.firstName,
              lastName: this.user.lastName,
              email: this.user.email
            }
          });
        }
      } catch (error) {
        this.error = this.$t("user.message.save-failed");
        return;
      } finally {
        this.isSaving = false;
      }

      this.$emit("unlocked");
      this.isEditing = false;
      if (this.isNew) {
        this.$emit("remove");
      }
    },
    async requestPassword() {
      if (this.isRequestingPassword) {
        return;
      }

      this.isRequestingPassword = true;
      this.error = null;

      try {
        const password = await this.$store.dispatch("requestPassword", {
          branchName: this.branch.name,
          loginName: this.user.loginName
        });
      } catch (error) {
        this.error = this.$t("user.message.new-password-failed");
        return;
      } finally {
        this.isRequestingPassword = false;
      }

      this.user.password = this.value.password;
    },
    cancelEdit() {
      this.$emit("unlocked");
      this.isEditing = false;
      this.error = null;
      if (this.isNew) {
        this.$emit("remove");
      } else {
        this.user = { ...this.value };
      }
    },
    async enableUser() {
      this.isActivating = true;
      await this.$store.dispatch("enableUser", {
        branchName: this.branch.name,
        loginName: this.user.loginName
      });
      this.user.active = true;
      this.isActivating = false;
    },
    async disableUser() {
      this.isDeactivating = true;
      await this.$store.dispatch("disableUser", {
        branchName: this.branch.name,
        loginName: this.user.loginName
      });
      this.user.active = false;
      this.isDeactivating = false;
    }
  }
};
</script>

<style lang="scss">
@import "~@apphq/rhenus-design-base/sass/variables.scss";
@import "~bulma/sass/form/shared.sass";
@import "~bulma/sass/form/tools.sass";
@import "~bulma/sass/elements/table.sass";
</style>

<style lang="scss" scoped>
@import "~@apphq/rhenus-design-base/sass/variables.scss";

.cell-controls {
  display: flex;

  .control:not(:last-child) {
    margin-right: 0.5em;
  }
}

.cell-text {
  padding: 0.75em 0.75em;
}

.is-placeholder {
  color: rgba($grey-darker, 0.3);
}

.input--name {
  width: 10em;
}

tr {
  &.is-danger {
    background-color: rgba($danger, 0.2);
  }
}
</style>
